import styled from "@emotion/styled";
import { useCallback, type FC } from "react";

import {
  MDSIconButton,
  MDSIconKind,
  MDSText,
  MDSTextVariant,
  MDSTooltip,
  MDSTooltipPlacement,
} from "@/mds";
import { AudioRecordingTranscriptionList } from "@/pages/audio-recordings/shared";
import { AudioRecordingObservable } from "@/store/audio-recordings";
import { actions } from "@/actions";
import { observer } from "mobx-react-lite";

type Props = {
  recording?: AudioRecordingObservable;
};

export const TranscriptionSectionContent: FC<Props> = observer(({ recording }) => {
  const handleCopyTranscript = useCallback(() => {
    if (recording) actions.copyRecordingTranscript({ recording });
  }, [recording]);

  const handleDownloadAudio = useCallback(() => {
    if (recording) actions.downloadRecordingAudio({ recording });
  }, [recording]);

  return (
    <Section>
      <SectionHeader>
        <MDSText variant={MDSTextVariant.TextSm}>Recording transcription</MDSText>
        {recording && (
          <Row>
            <MDSTooltip
              config={{
                label: "Download audio",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton iconKind={MDSIconKind.File} onClick={handleDownloadAudio} />
            </MDSTooltip>
            <MDSTooltip
              config={{
                label: "Copy transcript",
                placement: MDSTooltipPlacement.Top,
              }}
            >
              <MDSIconButton iconKind={MDSIconKind.Copy} onClick={handleCopyTranscript} />
            </MDSTooltip>
          </Row>
        )}
      </SectionHeader>
      <ScrollableContent>
        <AudioRecordingTranscriptionList
          transcriptTurns={recording?.transcriptTurns ?? []}
          emptyMessage="Transcript will appear here once recording starts."
        />
      </ScrollableContent>
    </Section>
  );
});

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ScrollableContent = styled.div`
  max-height: 280px;
  overflow-y: auto;
  padding-right: 8px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;
